import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Rating,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Checkbox,
  ListItemText,
  ListSubheader,
  ListItemIcon,
  Tooltip,
  Snackbar,
  SvgIcon,
  useTheme,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Search as SearchIcon,
  Instagram as InstagramIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Edit as EditIcon,
  Launch as LaunchIcon,
  DateRange as DateRangeIcon,
  ContentCopy as ContentCopyIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../app/store';
import { Comment } from '../../types/comments';
import { fetchPlaces, fetchPlaceWithReviews } from '../../features/googleReviews/googleReviewsSlice';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../config/api';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FilterProps } from './FeedbackDashboard';
import * as XLSX from 'xlsx';
import { UnifiedFeedback } from '../../types/unifiedFeedback';
import { FeedbackActionButtons } from '../../components/feedback/FeedbackActionButtons';
import { FeedbackDetailDialog } from '../../components/feedback/FeedbackDetailDialog';
import { ComplaintStatusDialog } from '../../components/feedback/ComplaintStatusDialog';
import { ComplaintHistoryDialog } from '../../components/feedback/ComplaintHistoryDialog';
import { getFeedbackExternalUrl } from '../../utils/feedbackUrls';

interface GoogleReview {
  id: string;
  text: string;
  stars: number;
  reviewer_name: string;
  published_at: string;
  reviewer_photo_url?: string;
  detailed_ratings?: { category: string; rating: number }[];
  review_images?: string[];
}

interface ProcessedBrand {
  brand_id: string;
  brand_name: string;
  platforms: ('instagram' | 'google' | 'tiktok' | 'facebook')[];
}

type ExportPlatform = 'instagram' | 'google' | 'tiktok';
type ExportFormat = 'xlsx' | 'csv';

const CHART_COLORS = {
  positive: '#4CAF50',
  neutral: '#FFC107',
  negative: '#F44336',
  instagram: '#E1306C',
  google: '#4285F4',
  tiktok: '#000000',
};

const EXPORT_PLATFORMS: ExportPlatform[] = ['instagram', 'google', 'tiktok'];
const EXPORT_PLATFORM_LABELS: Record<ExportPlatform, string> = {
  instagram: 'Instagram',
  google: 'Google',
  tiktok: 'TikTok',
};

interface EditDialogProps {
  open: boolean;
  comment: UnifiedFeedback | null;
  onClose: () => void;
  onSave: (id: string, updates: { sentiment?: string; category?: string; priority?: number | string }) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({ open, comment, onClose, onSave }) => {
  const [sentiment, setSentiment] = useState(comment?.sentiment || '');
  const [category, setCategory] = useState(comment?.category || '');
  const [priority, setPriority] = useState<number | string>(comment?.priority || '');

  useEffect(() => {
    setSentiment(comment?.sentiment || '');
    setCategory(comment?.category || '');
    setPriority(comment?.priority || '');
  }, [comment]);

  const handleSave = () => {
    if (comment?.id) {
      onSave(comment.id, {
        sentiment: sentiment || undefined,
        category: category || undefined,
        priority: priority || undefined,
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Feedback</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Sentiment</InputLabel>
            <Select
              value={sentiment}
              onChange={(e) => setSentiment(e.target.value)}
              label="Sentiment"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="positive">Positive</MenuItem>
              <MenuItem value="negative">Negative</MenuItem>
              <MenuItem value="neutral">Neutral</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
              <MenuItem value="feature">Feature</MenuItem>
              <MenuItem value="improvement">Improvement</MenuItem>
              <MenuItem value="question">Question</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              label="Priority"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value={1}>High</MenuItem>
              <MenuItem value={2}>Medium</MenuItem>
              <MenuItem value={3}>Low</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// Add TikTok icon component
const TikTokIcon = (props: any) => {
  const theme = useTheme();
  return (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{ color: theme.palette.text.primary, ...props.sx }}>
      <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 015.4 5.82C4.1 4.5 4.1 2.37 5.4 1.06 6.7-.25 8.84-.25 10.16 1.06c.63.62 1.109 1.39 1.39 2.24h3.17v7.54c0 1.45 1.19 2.64 2.64 2.64s2.64-1.19 2.64-2.64l-.01-3.89h1.98c1.2-.01 2.17-.98 2.17-2.18s-.97-2.17-2.17-2.17h-1.98c-.06-2.2-.93-4.27-2.41-5.79-1.91-1.91-4.54-2.84-7.18-2.64-4.47.34-8.14 3.98-8.52 8.45-.26 3.02.87 5.88 3.03 8.04s5.02 3.29 8.04 3.03c4.47-.38 8.11-4.05 8.45-8.52.19-2.64-.73-5.27-2.64-7.18z"/>
    </SvgIcon>
  );
};

interface UnifiedFeedbackPageProps {
  filterProps: FilterProps;
}

const UnifiedFeedbackPage = ({ filterProps }: UnifiedFeedbackPageProps): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const [unifiedFeedback, setUnifiedFeedback] = useState<UnifiedFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState(filterProps.searchTerm);
  const [searchTerm, setSearchTerm] = useState(filterProps.searchTerm);
  const [platformFilter, setPlatformFilter] = useState<'all' | 'instagram' | 'google' | 'tiktok' | 'facebook'>('all');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'priority'>('latest');
  const [sentimentFilter, setSentimentFilter] = useState<'all' | 'positive' | 'negative' | 'neutral'>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | '1' | '2' | '3'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<ProcessedBrand[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState<UnifiedFeedback | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('year'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [copySuccessMessage, setCopySuccessMessage] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<{ [key: string]: string }>({});
  const [submittingReply, setSubmittingReply] = useState<{ [key: string]: boolean }>({});
  const [replySuccess, setReplySuccess] = useState<string | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportPlatforms, setExportPlatforms] = useState<ExportPlatform[]>(EXPORT_PLATFORMS);
  const [exportBrands, setExportBrands] = useState<string[]>([]);
  const [exportStartDate, setExportStartDate] = useState<Dayjs | null>(dayjs().startOf('year'));
  const [exportEndDate, setExportEndDate] = useState<Dayjs | null>(dayjs());
  const [exportFormat, setExportFormat] = useState<ExportFormat>('xlsx');
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const { places, placeReviews } = useSelector((state: RootState) => state.googleReviews);
  const brandInfo = useSelector((state: RootState) => state.brand.brandInfo);

  useEffect(() => {
    void dispatch(fetchPlaces());
  }, [dispatch]);

  // Add new useEffect to fetch available brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        // If Facebook platform is selected, show empty brands
        if (platformFilter === 'facebook') {
          setAvailableBrands([]);
          return;
        }

        const response = await api.get('/feedback/brands');
        // Process brands to combine platforms for same brand
        const processedBrands = response.data.data.reduce((acc: ProcessedBrand[], currentBrand: any) => {
          const existingBrand = acc.find(b => b.brand_id === currentBrand.brand_id);
          if (existingBrand) {
            existingBrand.platforms = Array.from(new Set([...existingBrand.platforms, currentBrand.platform]));
          } else {
            acc.push({
              brand_id: currentBrand.brand_id,
              brand_name: currentBrand.brand_name,
              platforms: [currentBrand.platform]
            });
          }
          return acc;
        }, []);
        setAvailableBrands(processedBrands);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    void fetchBrands();
  }, [platformFilter]);

  // Add new useEffect to fetch available categories
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        // If Facebook platform is selected, show empty categories
        if (platformFilter === 'facebook') {
          setAvailableCategories([]);
          setLoadingCategories(false);
          return;
        }

        const response = await api.get('/feedback/categories');
        setAvailableCategories(response.data.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    void fetchCategories();
  }, [platformFilter]);

  const transformFeedbackItem = (item: UnifiedFeedback): UnifiedFeedback => {
    let platform: UnifiedFeedback['platform'];

    if (item.id.startsWith('instagram-')) {
      platform = 'instagram';
    } else if (item.id.startsWith('google-')) {
      platform = 'google';
    } else if (item.id.startsWith('facebook-')) {
      platform = 'facebook';
    } else if (item.id.startsWith('tiktok-')) {
      platform = 'tiktok';
    } else if (item.id.startsWith('gcc-')) {
      platform = 'gcc';
    } else if (item.id.startsWith('dm-')) {
      platform = 'dm';
    } else {
      platform = item.platform || 'instagram';
    }

    let video_id;
    let unique_id;
    let reply_comment_total;
    if (platform === 'tiktok') {
      video_id = item.brand_identifier;
      unique_id = item.author;
      reply_comment_total = 0;
    }

    let page_id;
    let page_name;
    let post_type;
    if (platform === 'facebook') {
      page_id = item.brand_identifier;
      page_name = item.brand_name;
      post_type = item.post_type || 'status';
    }

    return {
      ...item,
      platform,
      text: item.content || item.text,
      timestamp: item.created_at || item.timestamp,
      isVerified: item.isVerified ?? (item as UnifiedFeedback & { is_verified?: boolean }).is_verified,
      author: platform === 'instagram' ? item.author :
        platform === 'google' ? (item.reviewer_name || item.author) :
        (unique_id || item.author),
      authorImage: platform === 'instagram' ? (item.author_image || item.authorImage) :
        platform === 'google' ? (item.reviewer_photo_url || item.authorImage) :
        platform === 'facebook' ? (item.author_image || item.authorImage) :
        (item.avatar_thumbnail || item.authorImage),
      post_owner_username: platform === 'instagram' ? (item.brand_username || item.post_owner_username) : undefined,
      digg_count: platform === 'tiktok' ? item.likes : undefined,
      reply_comment_total: platform === 'tiktok' ? reply_comment_total : undefined,
      video_web_url: platform === 'tiktok' ? item.url : undefined,
      unique_id: platform === 'tiktok' ? unique_id : undefined,
      avatar_thumbnail: platform === 'tiktok' ? item.author_image : undefined,
      video_id: platform === 'tiktok' ? video_id : undefined,
      page_id: platform === 'facebook' ? page_id : undefined,
      page_name: platform === 'facebook' ? page_name : undefined,
      post_type: platform === 'facebook' ? post_type : undefined
    };
  };

  const getDefaultExportPlatforms = (): ExportPlatform[] => {
    if (platformFilter === 'instagram' || platformFilter === 'google' || platformFilter === 'tiktok') {
      return [platformFilter];
    }

    return EXPORT_PLATFORMS;
  };

  const handleOpenExportDialog = () => {
    setExportPlatforms(getDefaultExportPlatforms());
    setExportBrands(selectedBrands);
    setExportStartDate(startDate);
    setExportEndDate(endDate);
    setExportFormat('xlsx');
    setExportDialogOpen(true);
  };

  const getPriorityLabel = (priority?: number | string) => {
    if (priority === 1 || priority === '1') return 'High';
    if (priority === 2 || priority === '2') return 'Medium';
    if (priority === 3 || priority === '3') return 'Low';
    return priority ?? '';
  };

  const getExportUrl = (feedback: UnifiedFeedback) => getFeedbackExternalUrl(feedback) || '';

  const buildExportRows = (records: UnifiedFeedback[]) => records.map((feedback) => ({
    Platform: EXPORT_PLATFORM_LABELS[feedback.platform as ExportPlatform] || feedback.platform,
    Brand: feedback.brand_name || '',
    BrandID: feedback.brand_id || '',
    Account: feedback.account_name || '',
    Author: feedback.author || '',
    Content: feedback.text || '',
    Sentiment: feedback.sentiment || '',
    Category: feedback.category || '',
    Priority: getPriorityLabel(feedback.priority),
    Rating: feedback.rating ?? '',
    Likes: feedback.likes ?? feedback.digg_count ?? '',
    Verified: feedback.isVerified ? 'Yes' : 'No',
    CreatedAt: feedback.timestamp ? dayjs(feedback.timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
    URL: getExportUrl(feedback),
    SuggestedReply: feedback.suggested_reply || '',
  }));

  const fetchExportRecordsForPlatform = async (platform: ExportPlatform): Promise<UnifiedFeedback[]> => {
    const pageSize = 500;
    let currentPage = 1;
    let totalPages = 1;
    const records: UnifiedFeedback[] = [];

    while (currentPage <= totalPages) {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        platform,
        sentiment: sentimentFilter !== 'all' ? sentimentFilter : undefined,
        priority: (platform === 'instagram' || platform === 'tiktok') && priorityFilter !== 'all' ? priorityFilter : undefined,
        category: (platform === 'instagram' || platform === 'tiktok') && categoryFilter !== 'all' ? categoryFilter : undefined,
        sort_by: 'latest',
        brands: exportBrands.length > 0 ? exportBrands : undefined,
        start_date: exportStartDate?.toISOString(),
        end_date: exportEndDate?.toISOString(),
      };

      const response = await api.get<{ data: UnifiedFeedback[]; total: number }>('/feedback/unified', { params });
      records.push(...response.data.data.map(transformFeedbackItem));
      totalPages = Math.max(1, Math.ceil(response.data.total / pageSize));
      currentPage += 1;
    }

    return records;
  };

  const handleExport = async () => {
    if (exportPlatforms.length === 0) {
      setExportMessage('Select at least one platform to export.');
      return;
    }

    setExporting(true);

    try {
      const exportResults = await Promise.all(
        exportPlatforms.map((platform) => fetchExportRecordsForPlatform(platform))
      );

      const records = exportResults
        .flat()
        .sort((left, right) => dayjs(right.timestamp).valueOf() - dayjs(left.timestamp).valueOf());

      if (records.length === 0) {
        setExportMessage('No feedback matched the selected export filters.');
        return;
      }

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(buildExportRows(records));
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Feedback');

      const platformSuffix = exportPlatforms.length === 1
        ? exportPlatforms[0]
        : 'multi-platform';
      const dateSuffix = `${exportStartDate?.format('YYYYMMDD') || 'start'}-${exportEndDate?.format('YYYYMMDD') || 'end'}`;
      const fileName = `feedback-export-${platformSuffix}-${dateSuffix}.${exportFormat}`;

      if (exportFormat === 'csv') {
        XLSX.writeFile(workbook, fileName, { bookType: 'csv' });
      } else {
        XLSX.writeFile(workbook, fileName);
      }

      setExportDialogOpen(false);
      setExportMessage(`Exported ${records.length} feedback item${records.length === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Error exporting feedback:', error);
      setExportMessage('Failed to export feedback. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: rowsPerPage,
        search: searchTerm,
        platform: platformFilter !== 'all' ? platformFilter : undefined,
        sentiment: sentimentFilter !== 'all' ? sentimentFilter : undefined,
        priority: (platformFilter === 'instagram' || platformFilter === 'tiktok' || platformFilter === 'all') && priorityFilter !== 'all' ? priorityFilter : undefined,
        category: (platformFilter === 'instagram' || platformFilter === 'tiktok' || platformFilter === 'all') && categoryFilter !== 'all' ? categoryFilter : undefined,
        sort_by: sortBy,
        brands: selectedBrands.length > 0 ? selectedBrands : undefined,
        start_date: startDate?.toISOString(),
        end_date: endDate?.toISOString(),
      };

      // Check if Facebook platform is selected and show coming soon message
      if (platformFilter === 'facebook') {
        setUnifiedFeedback([]);
        setTotal(0);
        setError(null);
        return;
      }

      const response = await api.get<{ data: UnifiedFeedback[]; total: number }>('/feedback/unified', { params });
      const transformedFeedback = response.data.data.map(transformFeedbackItem);

      setUnifiedFeedback(transformedFeedback);
      setTotal(response.data.total);
      setError(null);
    } catch (err) {
      console.error('Error fetching unified feedback:', err);
      setError('Failed to fetch feedback data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchFeedback();
  }, [page, rowsPerPage, platformFilter, sortBy, sentimentFilter, priorityFilter, categoryFilter, selectedBrands, startDate, endDate, searchTerm]);

  // Update local filters when parent filters change
  useEffect(() => {
    setSearchInput(filterProps.searchTerm);
    setSearchTerm(filterProps.searchTerm);
    // Update other filter states as needed and trigger data fetch
    fetchFeedback();
  }, [filterProps]);

  // Handle search functionality
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1); // Reset to first page when searching
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleEditClick = (comment: UnifiedFeedback) => {
    setSelectedComment(comment);
    setEditDialogOpen(true);
  };

  const handleDetailClick = (comment: UnifiedFeedback) => {
    setSelectedComment(comment);
    setDetailDialogOpen(true);
  };

  const handleStatusClick = (comment: UnifiedFeedback) => {
    setSelectedComment(comment);
    setStatusDialogOpen(true);
  };

  const handleHistoryClick = (comment: UnifiedFeedback) => {
    setSelectedComment(comment);
    setHistoryDialogOpen(true);
  };

  const handleStatusSave = async (
    id: string,
    updates: { status: string; note: string; outlet_pic_id?: string }
  ) => {
    try {
      const response = await api.patch(`/feedback/complaint-status/${id}`, updates);
      setUnifiedFeedback((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                complaint_status: response.data.data.complaint_status,
                complaint_note: response.data.data.complaint_note,
                ...(response.data.data.outlet_pic_id && {
                  outlet_pic_id: response.data.data.outlet_pic_id,
                }),
                ...(response.data.data.outlet_pic_name && {
                  outlet_pic_name: response.data.data.outlet_pic_name,
                }),
              }
            : item
        )
      );
      setCopySuccessMessage('Status updated successfully!');
      setTimeout(() => setCopySuccessMessage(null), 3000);
    } catch (statusError) {
      console.error('Error updating status:', statusError);
      setCopySuccessMessage('Failed to update status');
      setTimeout(() => setCopySuccessMessage(null), 3000);
    }
  };

  const handleActionCopy = (comment: UnifiedFeedback) => {
    handleCopyToClipboard(comment.text || 'No text content', 'Feedback text');
  };

  const handleEditSave = async (id: string, updates: { sentiment?: string; category?: string; priority?: number | string }) => {
    try {
      const response = await api.patch(`/comments/${id}/metadata`, updates);
      
      // Update the comment in the local state
      setUnifiedFeedback((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, ...response.data } : item
        )
      );

      // Show success message
      // You can add a snackbar or other notification here
    } catch (error) {
      console.error('Error updating comment:', error);
      // Show error message
      // You can add a snackbar or other notification here
    }
  };

  // Add handler for sentiment filter change
  const handleSentimentFilterChange = (value: 'all' | 'positive' | 'negative' | 'neutral') => {
    setSentimentFilter(value);
    setPage(1); // Reset to first page when filter changes
  };

  // Add handler for priority filter change
  const handlePriorityFilterChange = (value: 'all' | '1' | '2' | '3') => {
    setPriorityFilter(value);
    setPage(1); // Reset to first page when filter changes
  };

  // Add handler for category filter change
  const handleCategoryFilterChange = (value: 'all' | string) => {
    setCategoryFilter(value as 'all' | string);
    setPage(1); // Reset to first page when filter changes
  };

  const handleCopyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccessMessage(`${type} copied to clipboard!`);
        setTimeout(() => setCopySuccessMessage(null), 3000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  const handleReplyChange = (id: string, text: string) => {
    setReplyText(prev => ({
      ...prev,
      [id]: text
    }));
  };
  
  const handleReplySubmit = async (feedback: UnifiedFeedback) => {
    if (!replyText[feedback.id] || replyText[feedback.id].trim() === '') {
      return;
    }
    
    setSubmittingReply(prev => ({
      ...prev,
      [feedback.id]: true
    }));
    
    try {
      let response;
      
      if (feedback.platform === 'instagram') {
        // Extract the numeric ID from the feedback ID (e.g., 'instagram-123' -> '123')
        const commentId = feedback.id.split('-')[1];
        
        response = await api.post('/feedback/instagram/reply', {
          comment_id: commentId,
          reply_text: replyText[feedback.id]
        });
      } else {
        // Handle other platforms (Google, TikTok) here
        response = await api.post(`/feedback/${feedback.platform}/reply`, {
          feedback_id: feedback.id,
          reply_text: replyText[feedback.id],
          platform: feedback.platform,
          // Additional platform-specific parameters
          ...(feedback.platform === 'tiktok' && { video_id: feedback.video_id }),
          ...(feedback.platform === 'google' && { place_id: feedback.place_id }),
          ...(feedback.platform === 'facebook' && { page_id: feedback.page_id }),
        });
      }
      
      // Clear the reply text for this feedback
      setReplyText(prev => ({
        ...prev,
        [feedback.id]: ''
      }));
      
      // Show success message
      setReplySuccess(`Reply submitted successfully to ${feedback.author}`);
      setTimeout(() => setReplySuccess(null), 3000);
      
      // Refresh the feedback list to show the updated reply status
      fetchFeedback();
      
    } catch (error) {
      console.error('Error submitting reply:', error);
      setError('Failed to submit reply. Please try again.');
    } finally {
      setSubmittingReply(prev => ({
        ...prev,
        [feedback.id]: false
      }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Unified Feedback
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ 
              backgroundColor: 'background.paper', 
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}>
              <Grid container spacing={2}>
                {/* Search Bar */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search feedback..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleSearch}
                            sx={{ p: 0.5 }}
                          >
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Platform and Brands Filter Row */}
                <Grid item xs={12} md={8}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 120, flexGrow: 1 }}>
                      <InputLabel>Platform</InputLabel>
                      <Select
                        value={platformFilter}
                        label="Platform"
                        onChange={(e) => setPlatformFilter(e.target.value as 'all' | 'instagram' | 'google' | 'tiktok' | 'facebook')}
                      >
                        <MenuItem value="all">All Platforms</MenuItem>
                        <MenuItem value="instagram">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InstagramIcon fontSize="small" sx={{ color: '#E1306C' }} />
                            <span>Instagram</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="google">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GoogleIcon fontSize="small" sx={{ color: '#4285F4' }} />
                            <span>Google</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="tiktok">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TikTokIcon />
                            <span>TikTok</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="facebook">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FacebookIcon fontSize="small" sx={{ color: '#1877F2' }} />
                            <span>Facebook</span>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120, flexGrow: 2 }}>
                      <InputLabel>Brands</InputLabel>
                      <Select
                        multiple
                        value={selectedBrands}
                        onChange={(e) => setSelectedBrands(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        label="Brands"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const brand = availableBrands.find(b => b.brand_id === value);
                              return brand && (
                                <Chip 
                                  key={value} 
                                  label={brand.brand_name}
                                  size="small"
                                  icon={
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      {brand.platforms.includes('instagram') && <InstagramIcon sx={{ fontSize: '1rem' }} />}
                                      {brand.platforms.includes('google') && <GoogleIcon sx={{ fontSize: '1rem' }} />}
                                      {brand.platforms.includes('tiktok') && <TikTokIcon sx={{ fontSize: '1rem' }} />}
                                      {brand.platforms.includes('facebook') && <FacebookIcon sx={{ fontSize: '1rem' }} />}
                                    </Box>
                                  }
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {availableBrands.map((brand) => (
                          <MenuItem key={brand.brand_id} value={brand.brand_id}>
                            <Checkbox checked={selectedBrands.includes(brand.brand_id)} />
                            <ListItemIcon>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {brand.platforms.includes('instagram') && <InstagramIcon />}
                                {brand.platforms.includes('google') && <GoogleIcon />}
                                {brand.platforms.includes('tiktok') && <TikTokIcon />}
                                {brand.platforms.includes('facebook') && <FacebookIcon />}
                              </Box>
                            </ListItemIcon>
                            <ListItemText primary={brand.brand_name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>

                {/* Filters and Date Range Row */}
                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Sentiment</InputLabel>
                        <Select
                          value={sentimentFilter}
                          label="Sentiment"
                          onChange={(e) => handleSentimentFilterChange(e.target.value as 'all' | 'positive' | 'negative' | 'neutral')}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="positive">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                              <span>Positive</span>
                            </Box>
                          </MenuItem>
                          <MenuItem value="neutral">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                              <span>Neutral</span>
                            </Box>
                          </MenuItem>
                          <MenuItem value="negative">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                              <span>Negative</span>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      
                      {/* Show Priority filter for Instagram and TikTok */}
                      {(platformFilter === 'instagram' || platformFilter === 'tiktok' || platformFilter === 'all') && (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Priority</InputLabel>
                          <Select
                            value={priorityFilter}
                            label="Priority"
                            onChange={(e) => handlePriorityFilterChange(e.target.value as 'all' | '1' | '2' | '3')}
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="1">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                                <span>High</span>
                              </Box>
                            </MenuItem>
                            <MenuItem value="2">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                                <span>Medium</span>
                              </Box>
                            </MenuItem>
                            <MenuItem value="3">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                                <span>Low</span>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      
                      {/* Show Category filter for Instagram and TikTok */}
                      {(platformFilter === 'instagram' || platformFilter === 'tiktok' || platformFilter === 'all') && (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={categoryFilter}
                            label="Category"
                            onChange={(e) => handleCategoryFilterChange(e.target.value as 'all' | string)}
                          >
                            <MenuItem value="all">All</MenuItem>
                            {loadingCategories ? (
                              <MenuItem disabled>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={20} />
                                  <span>Loading categories...</span>
                                </Box>
                              </MenuItem>
                            ) : (
                              availableCategories.map((category: string) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                    
                    <Box sx={{ ml: 'auto' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            slotProps={{ 
                              textField: { 
                                size: 'small',
                                sx: { width: { xs: '100%', sm: 'auto' } } 
                              } 
                            }}
                          />
                          <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            slotProps={{ 
                              textField: { 
                                size: 'small',
                                sx: { width: { xs: '100%', sm: 'auto' } } 
                              } 
                            }}
                            minDate={startDate || undefined}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleOpenExportDialog}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Export
                          </Button>
                        </Stack>
                      </LocalizationProvider>
                    </Box>
                  </Stack>
                </Grid>
                
                {/* Active Filters Display */}
                {(sentimentFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all' || selectedBrands.length > 0) && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                        Active filters:
                      </Typography>
                      
                      {sentimentFilter !== 'all' && (
                        <Chip 
                          label={`Sentiment: ${sentimentFilter}`} 
                          size="small" 
                          onDelete={() => handleSentimentFilterChange('all')}
                          color={
                            sentimentFilter === 'positive' ? 'success' : 
                            sentimentFilter === 'negative' ? 'error' : 'default'
                          }
                        />
                      )}
                      
                      {priorityFilter !== 'all' && (
                        <Chip 
                          label={`Priority: ${
                            priorityFilter === '1' ? 'High' :
                            priorityFilter === '2' ? 'Medium' :
                            'Low'
                          }`} 
                          size="small" 
                          onDelete={() => handlePriorityFilterChange('all')}
                          color={
                            priorityFilter === '1' ? 'error' :
                            priorityFilter === '2' ? 'warning' : 'success'
                          }
                        />
                      )}
                      
                      {categoryFilter !== 'all' && (
                        <Chip 
                          label={`Category: ${categoryFilter}`} 
                          size="small" 
                          onDelete={() => handleCategoryFilterChange('all')}
                          color="info"
                        />
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
        </Grid>

        <Stack spacing={2}>
          {platformFilter === 'facebook' ? (
            <Card>
              <CardContent>
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center" 
                  py={8}
                  textAlign="center"
                >
                  <FacebookIcon sx={{ fontSize: 64, color: '#1877F2', mb: 2 }} />
                  <Typography variant="h5" color="textSecondary" gutterBottom>
                    No Data
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Facebook integration is coming soon!
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            unifiedFeedback.map((feedback) => (
            <Card key={feedback.id}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Avatar
                    src={feedback.avatar_thumbnail}
                    alt={feedback.author}
                    sx={{ width: 40, height: 40, flexShrink: 0 }}
                  >
                    {feedback.author[0]}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      gap={1}
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1} minWidth={0}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {feedback.platform === 'instagram' ? (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Link
                                href={`https://www.instagram.com/${feedback.author}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: 'inherit',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                @{feedback.author}
                              </Link>
                            </Box>
                          ) : feedback.platform === 'tiktok' ? (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Link
                                href={feedback.video_web_url ? feedback.video_web_url : '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: 'inherit',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                @{feedback.author}
                              </Link>
                            </Box>
                          ) : feedback.platform === 'facebook' ? (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Link
                                href={feedback.url ? feedback.url : '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: 'inherit',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                @{feedback.author}
                              </Link>
                            </Box>
                          ) : (
                            feedback.author
                          )}
                        </Typography>
                        {feedback.isVerified && (
                          <Chip label="Verified" size="small" color="primary" />
                        )}
                        {feedback.platform === 'instagram' ? (
                          <>
                            <Chip
                              icon={<InstagramIcon />}
                              label="Instagram"
                              size="small"
                              color="secondary"
                            />
                          </>
                        ) : feedback.platform === 'tiktok' ? (
                          <>
                            <Chip
                              icon={<TikTokIcon />}
                              label="TikTok"
                              size="small"
                              sx={{ 
                                backgroundColor: '#000000',
                                color: '#ffffff'
                              }}
                            />
                          </>
                        ) : feedback.platform === 'facebook' ? (
                          <>
                            <Chip
                              icon={<FacebookIcon />}
                              label="Facebook"
                              size="small"
                              sx={{ 
                                backgroundColor: '#1877F2',
                                color: '#ffffff'
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <Chip
                              icon={<GoogleIcon />}
                              label="Google Review"
                              size="small"
                              color="primary"
                            />
                          </>
                        )}
                        {feedback.sentiment && (
                          <Chip
                            label={`Sentiment: ${feedback.sentiment}`}
                            size="small"
                            color={
                              feedback.sentiment.toLowerCase() === 'positive'
                                ? 'success'
                                : feedback.sentiment.toLowerCase() === 'negative'
                                ? 'error'
                                : 'default'
                            }
                          />
                        )}
                      </Box>
                      <FeedbackActionButtons
                        feedback={feedback}
                        layout="row"
                        compact
                        onViewDetail={handleDetailClick}
                        onEditStatus={handleStatusClick}
                        onViewHistory={handleHistoryClick}
                        onEditMetadata={handleEditClick}
                        onCopy={handleActionCopy}
                        showMetadataEdit={
                          feedback.platform === 'instagram' ||
                          feedback.platform === 'tiktok' ||
                          feedback.platform === 'facebook'
                        }
                      />
                    </Box>

                      {feedback.platform === 'instagram' ? (
                        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                          {feedback.category && (
                            <Chip
                              label={`Category: ${feedback.category}`}
                              size="small"
                              color="info"
                            />
                          )}
                          {feedback.priority && (
                            <Chip
                              label={`Priority: ${
                                feedback.priority === 1 || feedback.priority === '1' ? 'High' :
                                feedback.priority === 2 || feedback.priority === '2' ? 'Medium' :
                                feedback.priority === 3 || feedback.priority === '3' ? 'Low' : feedback.priority
                              }`}
                              size="small"
                              color={
                                feedback.priority === 1 || feedback.priority === '1' ? 'error' :
                                feedback.priority === 2 || feedback.priority === '2' ? 'warning' :
                                feedback.priority === 3 || feedback.priority === '3' ? 'success' : 'default'
                              }
                            />
                          )}
                          {feedback.likes !== undefined && feedback.likes > 0 && (
                            <Chip
                              label={`${feedback.likes} likes`}
                              size="small"
                            />
                          )}
                        </Box>
                      ) : feedback.platform === 'tiktok' ? (
                        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                          {feedback.category && (
                            <Chip
                              label={`Category: ${feedback.category}`}
                              size="small"
                              color="info"
                            />
                          )}
                          {feedback.priority && (
                            <Chip
                              label={`Priority: ${feedback.priority}`}
                              size="small"
                              color={feedback.priority === 'high' ? 'error' : feedback.priority === 'medium' ? 'warning' : 'info'}
                            />
                          )}
                          {feedback.digg_count && (
                            <Chip
                              label={`${feedback.digg_count} likes`}
                              size="small"
                            />
                          )}
                        </Box>
                      ) : feedback.platform === 'facebook' ? (
                        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                          {feedback.category && (
                            <Chip
                              label={`Category: ${feedback.category}`}
                              size="small"
                              color="info"
                            />
                          )}
                          {feedback.priority && (
                            <Chip
                              label={`Priority: ${
                                feedback.priority === 1 || feedback.priority === '1' ? 'High' :
                                feedback.priority === 2 || feedback.priority === '2' ? 'Medium' :
                                feedback.priority === 3 || feedback.priority === '3' ? 'Low' : feedback.priority
                              }`}
                              size="small"
                              color={
                                feedback.priority === 1 || feedback.priority === '1' ? 'error' :
                                feedback.priority === 2 || feedback.priority === '2' ? 'warning' :
                                feedback.priority === 3 || feedback.priority === '3' ? 'success' : 'default'
                              }
                            />
                          )}
                          <Stack direction="row" spacing={1} alignItems="center">
                            {feedback.digg_count !== undefined && (
                              <Chip
                                size="small"
                                label={feedback.digg_count}
                                icon={<ThumbUpIcon fontSize="small" />}
                                variant="outlined"
                              />
                            )}
                            {feedback.reply_comment_total !== undefined && (
                              <Chip
                                size="small"
                                label={feedback.reply_comment_total}
                                icon={<CommentIcon fontSize="small" />}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>
                      ) : (
                        <>
                          <Box mb={1}>
                            <Rating value={feedback.rating} readOnly />
                          </Box>
                          {feedback.detailedRatings && feedback.detailedRatings.length > 0 && (
                            <Box display="flex" gap={2} mb={1} flexWrap="wrap">
                              {feedback.detailedRatings
                                .filter(rating => 
                                  ['Atmosphere', 'Food', 'Service'].includes(rating.category)
                                )
                                .map(rating => (
                                  <Box key={rating.category}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {rating.category}
                                    </Typography>
                                    <Rating value={rating.rating} readOnly size="small" />
                                  </Box>
                                ))
                              }
                            </Box>
                          )}
                        </>
                      )}

                      <Typography variant="body1" paragraph>
                        {feedback.text}
                      </Typography>

                      {/* Display suggested reply for Instagram */}
                      {feedback.platform === 'instagram' && feedback.suggested_reply && (
                        <Box 
                          sx={{ 
                            mb: 2, 
                            p: 2, 
                            backgroundColor: 'rgba(225, 48, 108, 0.05)',
                            borderLeft: '4px solid #E1306C',
                            borderRadius: 1,
                            position: 'relative'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#E1306C' }}>
                              Suggested Reply:
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopyToClipboard(feedback.suggested_reply || '', 'Suggested reply')}
                                sx={{ color: '#E1306C' }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="body2" paragraph sx={{ mb: 0, whiteSpace: 'pre-wrap' }}>
                            {feedback.suggested_reply}
                          </Typography>
                        </Box>
                      )}

                      {/* Display suggested reply for TikTok */}
                      {feedback.platform === 'tiktok' && feedback.suggested_reply && (
                        <Box 
                          sx={{ 
                            mb: 2, 
                            p: 2, 
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            borderLeft: '4px solid #000000',
                            borderRadius: 1,
                            position: 'relative'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#000000' }}>
                              Suggested Reply:
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopyToClipboard(feedback.suggested_reply || '', 'Suggested reply')}
                                sx={{ color: '#000000' }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="body2" paragraph sx={{ mb: 0, whiteSpace: 'pre-wrap' }}>
                            {feedback.suggested_reply}
                          </Typography>
                        </Box>
                      )}

                      {/* Display suggested reply for Facebook */}
                      {feedback.platform === 'facebook' && feedback.suggested_reply && (
                        <Box 
                          sx={{ 
                            mb: 2, 
                            p: 2, 
                            backgroundColor: 'rgba(24, 119, 242, 0.05)',
                            borderLeft: '4px solid #1877F2',
                            borderRadius: 1,
                            position: 'relative'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#1877F2' }}>
                              Suggested Reply:
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopyToClipboard(feedback.suggested_reply || '', 'Suggested reply')}
                                sx={{ color: '#1877F2' }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="body2" paragraph sx={{ mb: 0, whiteSpace: 'pre-wrap' }}>
                            {feedback.suggested_reply}
                          </Typography>
                        </Box>
                      )}

                      {/* Add reply display section before the reply form */}
                      {feedback.replies && feedback.replies.length > 0 && (
                        <Box sx={{ mt: 2, ml: 4 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Replies
                          </Typography>
                          <Stack spacing={2}>
                            {feedback.replies.map((reply) => (
                              <Box
                                key={reply.id}
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  p: 2,
                                  borderRadius: 1,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                }}
                              >
                                <Avatar
                                  src={reply.author_image}
                                  alt={reply.author}
                                  sx={{ width: 32, height: 32 }}
                                >
                                  {reply.author[0]}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="subtitle2">
                                      {reply.author}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {dayjs(reply.timestamp).format('MMM D, YYYY h:mm A')}
                                    </Typography>
                                    {reply.likes !== undefined && reply.likes > 0 && (
                                      <Typography variant="caption" color="text.secondary">
                                        • {reply.likes} likes
                                      </Typography>
                                    )}
                                  </Box>
                                  <Typography variant="body2">
                                    {reply.text}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Add divider before reply form if there are replies */}
                      {feedback.replies && feedback.replies.length > 0 && (
                        <Divider sx={{ my: 2 }} />
                      )}

                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="caption" color="text.secondary">
                            {feedback.platform === 'instagram' 
                              ? dayjs(feedback.timestamp).format('MMMM D, YYYY')
                              : dayjs(feedback.timestamp).format('MMMM D, YYYY h:mm A')}
                          </Typography>
                          {feedback.likes !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              {feedback.likes} likes
                            </Typography>
                          )}
                        </Box>
                        
                        {feedback.platform === 'instagram' && feedback.short_code ? (
                          <Link
                            href={`https://www.instagram.com/p/${feedback.short_code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#E1306C',
                              textDecoration: 'none',
                              borderRadius: 1,
                              padding: '4px 8px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(225, 48, 108, 0.1)',
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            <InstagramIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              View on {feedback.account_name}
                            </Typography>
                            <LaunchIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                          </Link>
                        ) : feedback.platform === 'google' ? (
                          <Link
                            href={`${feedback.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#4285F4',
                              textDecoration: 'none',
                              borderRadius: 1,
                              padding: '4px 8px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(66, 133, 244, 0.1)',
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            <GoogleIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {feedback.account_name || 'View on Google Maps'}
                            </Typography>
                            <LaunchIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                          </Link>
                        ) : feedback.platform === 'tiktok' && feedback.video_web_url ? (
                          <Link
                            href={feedback.video_web_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#000000',
                              textDecoration: 'none',
                              borderRadius: 1,
                              padding: '4px 8px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            <TikTokIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              View on TikTok
                            </Typography>
                            <LaunchIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                          </Link>
                        ) : feedback.platform === 'facebook' && feedback.url ? (
                          <Link
                            href={feedback.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#1877F2',
                              textDecoration: 'none',
                              borderRadius: 1,
                              padding: '4px 8px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(24, 119, 242, 0.1)',
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            <FacebookIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              View on Facebook
                            </Typography>
                            <LaunchIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                          </Link>
                        ) : null}
                      </Box>

                      {/* Add reply form below existing content */}
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          placeholder={`Reply to ${feedback.author}...`}
                          value={replyText[feedback.id] || ''}
                          onChange={(e) => handleReplyChange(feedback.id, e.target.value)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  disabled={!replyText[feedback.id] || submittingReply[feedback.id]}
                                  onClick={() => handleReplySubmit(feedback)}
                                  sx={{ 
                                    borderRadius: '20px',
                                    ml: 1
                                  }}
                                >
                                  {submittingReply[feedback.id] ? (
                                    <CircularProgress size={24} color="inherit" />
                                  ) : (
                                    'Reply'
                                  )}
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '20px',
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                            }
                          }}
                        />
                      </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            ))
          )}
        </Stack>

        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={Math.ceil(total / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Box>

      <EditDialog
        open={editDialogOpen}
        comment={selectedComment}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedComment(null);
        }}
        onSave={handleEditSave}
      />

      <FeedbackDetailDialog
        open={detailDialogOpen}
        feedback={selectedComment}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedComment(null);
        }}
      />

      <ComplaintStatusDialog
        open={statusDialogOpen}
        feedback={selectedComment}
        onClose={() => setStatusDialogOpen(false)}
        onSave={handleStatusSave}
      />

      <ComplaintHistoryDialog
        open={historyDialogOpen}
        feedback={selectedComment}
        onClose={() => setHistoryDialogOpen(false)}
      />

      <Dialog open={exportDialogOpen} onClose={() => !exporting && setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Feedback</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Platforms</InputLabel>
              <Select
                multiple
                value={exportPlatforms}
                label="Platforms"
                onChange={(e) => setExportPlatforms(e.target.value as ExportPlatform[])}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((platform) => (
                      <Chip
                        key={platform}
                        size="small"
                        label={EXPORT_PLATFORM_LABELS[platform as ExportPlatform]}
                      />
                    ))}
                  </Box>
                )}
              >
                {EXPORT_PLATFORMS.map((platform) => (
                  <MenuItem key={platform} value={platform}>
                    <Checkbox checked={exportPlatforms.includes(platform)} />
                    <ListItemText primary={EXPORT_PLATFORM_LABELS[platform]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Brands</InputLabel>
              <Select
                multiple
                value={exportBrands}
                label="Brands"
                onChange={(e) => setExportBrands(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        All brands
                      </Typography>
                    ) : (
                      selected.map((value) => {
                        const brand = availableBrands.find((item) => item.brand_id === value);
                        return brand ? <Chip key={value} size="small" label={brand.brand_name} /> : null;
                      })
                    )}
                  </Box>
                )}
              >
                {availableBrands.map((brand) => (
                  <MenuItem key={brand.brand_id} value={brand.brand_id}>
                    <Checkbox checked={exportBrands.includes(brand.brand_id)} />
                    <ListItemText primary={brand.brand_name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <DatePicker
                  label="Start Date"
                  value={exportStartDate}
                  onChange={(newValue) => setExportStartDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                  label="End Date"
                  value={exportEndDate}
                  onChange={(newValue) => setExportEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  minDate={exportStartDate || undefined}
                />
              </Stack>
            </LocalizationProvider>

            <FormControl fullWidth size="small">
              <InputLabel>File Format</InputLabel>
              <Select
                value={exportFormat}
                label="File Format"
                onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
              >
                <MenuItem value="xlsx">Excel (.xlsx)</MenuItem>
                <MenuItem value="csv">CSV (.csv)</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary">
              Export also uses the current search, sentiment, category, and priority filters from this page.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Snackbar for reply success message */}
      <Snackbar
        open={!!replySuccess}
        autoHideDuration={3000}
        onClose={() => setReplySuccess(null)}
        message={replySuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={!!copySuccessMessage}
        autoHideDuration={3000}
        onClose={() => setCopySuccessMessage(null)}
        message={copySuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Snackbar
        open={!!exportMessage}
        autoHideDuration={4000}
        onClose={() => setExportMessage(null)}
        message={exportMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default UnifiedFeedbackPage; 